/**
 * Helper: given a role definition (`base_role` + `permissions`), return
 * the sidebar link tree the user would see AFTER the Layout filter runs.
 *
 * Extracted so both `<Layout>` (real render) and the role-editor's
 * Sidebar Preview panel share the same rules — otherwise the "what
 * tabs will this role see?" preview drifts from what the app actually
 * renders after the role saves.
 *
 * Args:
 *   sidebar        — the SIDEBAR_LINKS array
 *   base_role      — role's base built-in slug ("supervisor" / "admin" / etc.)
 *   permissions    — array of permission slugs granted to the role
 *
 * Returns a pruned copy of the sidebar tree. Groups whose children were
 * all filtered out are dropped so the preview mirrors iter277 behaviour.
 */
export function previewSidebarForRole(sidebar, base_role, permissions) {
  const perms = new Set(permissions || []);
  const isClientTier = ["client", "client_admin", "regional_manager", "site_manager"].includes(base_role);
  const isGuard = base_role === "guard" || base_role === "officer";

  const passesFlags = (item) => {
    if (item.clientOnly && !isClientTier) return false;
    if (item.hideForClient && isClientTier) return false;
    if (isGuard && !item.guardOK) return false;
    if (item.superOnly && base_role !== "super_admin") return false;
    if (item.adminOnly && !(base_role === "admin" || base_role === "super_admin")) return false;
    if (
      item.adminScope &&
      !["admin", "super_admin", "chief_security_officer", "operations_manager"].includes(base_role)
    ) return false;
    if (
      item.supAdminOnly &&
      !(base_role === "admin" || base_role === "supervisor" || base_role === "super_admin")
    ) return false;
    return true;
  };

  const passesPerm = (item) => {
    // super_admin + admin bypass fine-grained perms (matches Protected route logic).
    if (base_role === "super_admin" || base_role === "admin") return true;
    if (!item.permission) return true; // leaf gated only by flags
    return perms.has(item.permission);
  };

  const keep = (item) => passesFlags(item) && passesPerm(item);

  const pruned = sidebar
    .map((entry) => {
      if (Array.isArray(entry.children)) {
        const kids = entry.children.filter(keep);
        return { ...entry, children: kids };
      }
      return entry;
    })
    .filter((entry) => {
      if (!keep(entry)) return false;
      if (Array.isArray(entry.children) && entry.children.length === 0) return false;
      return true;
    });

  return pruned;
}
